import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Mail, ArrowLeft, Loader2 } from "lucide-react";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(err.message || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col justify-between p-6 md:p-12 relative overflow-hidden font-sans">
      {/* Decorative Red Laser Flare */}
      <div className="absolute top-0 right-1/4 w-[1px] h-[500px] bg-gradient-to-b from-[#FF3B30] to-transparent opacity-20 pointer-events-none" />
      
      {/* Header */}
      <header className="flex justify-between items-center z-10">
        <Link 
          to="/" 
          className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-mono text-[#A1A1AA] hover:text-white transition-colors duration-200"
          data-testid="back-to-home-link"
        >
          <ArrowLeft className="w-4 h-4 text-[#FF3B30]" /> Back to Ledger
        </Link>
        <span className="text-xs font-mono text-[#FF3B30]">PREMI-SKILLS // SECURED</span>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center py-12 z-10">
        <div className="w-full max-w-md bg-[#121212] border border-white/10 p-8 md:p-10 rounded-none relative">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.25em] font-mono text-[#FF3B30] mb-2">CONTROL PANEL</div>
            <h1 className="text-3xl font-black tracking-tighter">AUTHENTICATE</h1>
            <p className="text-[#A1A1AA] text-sm mt-2 font-mono">Premi-Skills administrative console access.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div 
                className="bg-red-950/40 border border-[#FF3B30]/30 text-[#FF3B30] p-4 text-xs font-mono"
                data-testid="login-error-message"
              >
                [ERROR]: {error}
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-xs uppercase tracking-wider font-mono text-[#A1A1AA]">Admin Email</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[#A1A1AA]">
                  <Mail className="w-4 h-4" />
                </span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@primi-data.com"
                  className="w-full bg-black border border-white/10 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] py-3 pl-10 pr-4 text-sm text-white rounded-none outline-none transition-all duration-200"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-xs uppercase tracking-wider font-mono text-[#A1A1AA]">Password</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[#A1A1AA]">
                  <Lock className="w-4 h-4" />
                </span>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-black border border-white/10 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] py-3 pl-10 pr-4 text-sm text-white rounded-none outline-none transition-all duration-200"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-white text-black hover:bg-[#FF3B30] hover:text-white font-mono font-bold py-3 transition-colors duration-200 uppercase tracking-wider text-xs flex items-center justify-center gap-2 rounded-none"
              data-testid="login-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Authorizing...
                </>
              ) : (
                "Request Access"
              )}
            </button>
          </form>
        </div>
      </main>

      {/* Footer */}
      <footer className="text-center text-xs font-mono text-[#A1A1AA] z-10 border-t border-white/5 pt-6">
        &copy; 2026 PREMI-SKILLS. ALL RIGHTS RESERVED. EXCLUSIVE LICENSING PROTOCOL.
      </footer>
    </div>
  );
}
